import React, { useState } from 'react';
import Navbar from '../components/NavbarLanding';
import '../Style/Analysis.css'
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import arenaIcon from '../assets/solar_eye-linear.png';
import blindSpotIcon from '../assets/mdi_eye-off-outline.png';
import hiddenAreaIcon from '../assets/tdesign_member.png';
import unknownAreaIcon from '../assets/ri_question-line.png';

const Analysis = () => {
    const navigate = useNavigate();

    const cards = [
        {
            title: 'Open Area (Arena)',
            subtitle: 'Known to Self & Others',
            percentage: '25% of responses',
            icon: arenaIcon,
            bgColor: 'bg-red-100',
            iconBg: '#FFB8B3',
            highlightBg: 'bg-red-200',
            textColor: '#FFB8B3',
            responses: [
                'Analytical and logical',
                'Data-driven and factual',
                'The careful planner',
                'Independence and freedom',
                'Big-picture and strategic',
            ],
            tip: 'Expand this area through open communication and feedback.',
        },
        {
            title: 'Blind Spot',
            subtitle: 'Unknown to Self, Known to Others',
            percentage: '15% of responses',
            icon: blindSpotIcon,
            bgColor: 'bg-purple-100',
            iconBg: '#CE93D8',
            highlightBg: 'bg-purple-200',
            textColor: '#CE93D8',
            responses: [
                'You’re more reserved than you think',
                'You’re not open when you realize',
                'You repeat yourself when passionate',
                'You rarely express what you prefer',
            ],
            tip: 'Seek feedback from others to reduce blind spots.',
        },
        {
            title: 'Hidden Area',
            subtitle: 'Known to Self, Unknown to Others',
            percentage: '30% of responses',
            icon: hiddenAreaIcon,
            bgColor: 'bg-green-100',
            iconBg: '#A5D6A7',
            highlightBg: 'bg-green-200',
            textColor: '#A5D6A7',
            responses: [
                'Desire for privacy',
                'Deep self-reflection',
                'Private emotional experiences',
            ],
            tip: 'Share appropriate personal insights to build authenticity.',
        },
        {
            title: 'Unknown Area',
            subtitle: 'Unknown to Self & Others',
            percentage: '30% of responses',
            icon: unknownAreaIcon,
            bgColor: 'bg-blue-100',
            iconBg: '#90CAF9',
            highlightBg: 'bg-blue-200',
            textColor: '#90CAF9',
            responses: [
                'Unexplored potential',
                'Surprising creativity',
                'Hidden resilience',
            ],
            tip: 'Try new experiences to uncover hidden potential.',
        },
    ];

    const JohariCard = ({ card }) => (
        <div className={`rounded-xl p-6 ${card.bgColor}  shadow-md mt-5 `} style={{ width: '43%', minWidth: '333px' }} >
            <div className="flex items-center gap-3 mb-3">
                <div className={`h-12 w-12 flex items-center justify-center rounded-lg `} style={{ backgroundColor: card.iconBg }}>
                    <img src={card.icon} alt="icon" className="w-6 h-6" />
                </div>
                <div >
                    <h2 className={`font-bold text-lg `} style={{ color: card.textColor }}>{card.title}</h2>
                    <p className="text-sm text-gray-700">{card.subtitle}</p>
                </div>
            </div>

            <span className={`inline-block text-xs ${card.highlightBg} text-gray-700 px-3 py-1 rounded-full font-medium mb-4`}>
                {card.percentage}
            </span>

            <p className="text-sm text-gray-700 mb-4">
                Traits and behaviors that are apparent to both you and others. This represents your public self.
            </p>

            <h3 className="font-bold text-lg text-gray-800 mb-2" style={{ color: card.textColor }}>Your Responses:</h3>
            <div className="space-y-2 mb-4">
                {card.responses.map((response, idx) => (
                    <div key={idx} className={`w-full px-3 py-2 rounded-md font-medium text-sm text-gray-800 ${card.highlightBg}`}>
                        {response}
                    </div>
                ))}
            </div>

            {/* Growth Tip */}
            <div className={`rounded-md p-3 ${card.highlightBg}`}>
                <p className="text-sm font-semibold text-gray-700">💡 Growth Tip:</p>
                <p className="text-sm text-gray-800">{card.tip}</p>
            </div>
        </div>
    );
    return (
        <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
            <Navbar />

            <section className="w-full flex flex-col items-center justify-center mt-28 pb-10 bg-transparent px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-7xl mx-auto">
                    <h2
                        className="text-center mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
                        style={{
                            fontFamily: 'Roboto Slab, serif',
                            fontWeight: 700,
                            fontStyle: 'normal',
                            lineHeight: '1.2',
                            letterSpacing: '0%',
                        }}
                    >
                        Your Johari Window Analysis
                    </h2>
                    <p className="text-gray-600 text-sm sm:text-base md:text-lg text-center max-w-4xl mx-auto">
                        <span style={{ textAlign: 'center', display: 'block' }}>
                            Understand your self-awareness and blind spots </span>
                    </p>
                </div>
                <div className='johari-section'>
                    <p>
                        The Johari Window helps you understand how self-perception and others' perceptions create four areas of awareness. The goal is to expand your Open Area while reducing blind spots and exploring unknown potential.
                    </p>
                </div>
                <div className="flex flex-wrap p-6 max-w-6xl mx-auto gap-5 items-center" style={{ justifyContent: 'center', alignItems: 'baseline' }}>
                    {cards.map((card, i) => (
                        <JohariCard key={i} card={card} />
                    ))}
                </div>
                <div className="bg-[#fef3e2] rounded-xl shadow-lg p-6 max-w-4xl w-full space-y-6 mt-4">
                    <div className="bg-[#fef3e2] p-5 rounded-md">
                        <h2 className="text-orange-600 text-xl font-bold flex items-center gap-2 mb-2">
                            <span className="text-2xl">🔍</span> Your Self-Awareness Analysis
                        </h2>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <h3 className="font-semibold text-gray-700 mb-1">Strengths:</h3>
                                <ul className="list-disc list-inside text-sm text-gray-800">
                                    <li>Good understanding of your private self</li>
                                    <li>Awareness of your potential for growth</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-700 mb-1">Growth Opportunities:</h3>
                                <ul className="list-disc list-inside text-sm text-gray-800">
                                    <li>Explore new experiences to discover hidden potential</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-orange-100 p-5 rounded-md">
                        <h3 className="text-orange-700 font-bold text-md mb-3">Recommended Actions:</h3>
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-800">
                            <div>
                                <p className="font-bold">To expand Open Area:</p>
                                <p>Practice active listening and ask for specific feedback from colleagues and friends.</p>
                            </div>
                            <div>
                                <p className="font-bold">To reduce Blind Spots:</p>
                                <p>Use 360-degree feedback tools and be open to constructive criticism.</p>
                            </div>
                            <div>
                                <p className="font-bold">To manage Hidden Area:</p>
                                <p>Share appropriate personal insights to build trust and authenticity.</p>
                            </div>
                            <div>
                                <p className="font-bold">To explore Unknown Area:</p>
                                <p>Try new activities, take personality assessments, and seek diverse experiences.</p>
                            </div>
                        </div>
                    </div>


                </div>
                <div className="flex justify-center gap-4 pt-2 mt-10">
                    <button className="bg-gradient-to-r from-pink-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm shadow-md">
                        Start Over
                    </button>
                    <button className="border border-gray-300 px-4 py-2 rounded-full text-sm text-gray-700 shadow-sm hover:bg-gray-50">
                        Try IKIGAI Test
                    </button>
                </div>
            </section>


            <Footer />
        </div>
    );
};

export default Analysis; 